import React, { useState } from 'react';

const ClinicBasic: React.FC = () => {
  const [isBooked, setIsBooked] = useState(false);

  const handleBooking = (e: React.FormEvent) => {
    e.preventDefault();
    setIsBooked(true);
    setTimeout(() => setIsBooked(false), 4000);
  };

  const services = [
    { name: "General Checkup", icon: "🩺", desc: "Comprehensive health checkups for all age groups." },
    { name: "Root Canal Treatment", icon: "🦷", desc: "Painless RCT by experienced endodontists." },
    { name: "Pediatric Care", icon: "👶", desc: "Friendly and safe medical care for children." },
    { name: "Dental Implants", icon: "😁", desc: "Permanent solution for missing teeth with premium implants." },
    { name: "Skin & Dermatology", icon: "✨", desc: "Advanced skincare treatments and acne solutions." },
    { name: "Digital X-Ray", icon: "🩻", desc: "Quick and accurate diagnostics with minimal radiation." }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-sans scroll-smooth">
      
      {/* 1. TOP BAR (Emergency & Timings - Crucial for Clinics) */}
      <div className="bg-teal-700 text-white text-xs sm:text-sm py-2 px-6 flex flex-col sm:flex-row justify-between items-center gap-2">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">🕒 Mon-Sat: 9:00 AM - 8:00 PM</span>
          <span className="hidden sm:inline">|</span>
          <span className="flex items-center gap-1">📍 Sector 14, City Center</span>
        </div>
        <div className="font-bold tracking-widest uppercase">
          Emergency: <a href="tel:+919876543210" className="hover:text-teal-200">+91 98765 43210</a>
        </div>
      </div>

      {/* 2. NAVBAR */}
      <nav className="bg-white sticky top-0 z-50 shadow-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer">
            <span className="text-3xl text-teal-600">✚</span>
            <div className="leading-tight">
              <span className="text-xl font-bold text-slate-900 tracking-tight block">ApexCare</span>
              <span className="text-[10px] uppercase tracking-widest text-teal-600 font-bold">Multispecialty Clinic</span>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-8 font-semibold text-slate-600 text-sm">
            <a href="#about" className="hover:text-teal-600 transition">Our Doctor</a>
            <a href="#services" className="hover:text-teal-600 transition">Treatments</a>
            <a href="#contact" className="hover:text-teal-600 transition">Contact</a>
          </div>

          <a href="#appointment" className="bg-teal-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-teal-700 transition shadow-md hidden sm:block">
            Book Appointment
          </a>
        </div>
      </nav>

      {/* 3. HERO SECTION (Trust Building) */}
      <header className="bg-white py-16 lg:py-24 px-6 border-b border-slate-100">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <span className="inline-block bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border border-teal-100">
              Your Health, Our Priority
            </span>
            <h1 className="text-4xl lg:text-6xl font-bold text-slate-900 leading-tight">
              Expert Medical & <br /> <span className="text-teal-600">Dental Care</span> You Can Trust.
            </h1>
            <p className="text-lg text-slate-500 max-w-lg leading-relaxed">
              Providing compassionate, high-quality healthcare using the latest technology. Get the right diagnosis and treatment under one roof.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a href="#appointment" className="bg-teal-600 text-white text-center px-8 py-3.5 rounded-lg font-bold hover:bg-teal-700 transition shadow-lg shadow-teal-600/20">
                Book Consultation
              </a>
              <a href="#services" className="bg-slate-50 text-slate-700 border border-slate-200 text-center px-8 py-3.5 rounded-lg font-bold hover:bg-slate-100 transition">
                View Services
              </a>
            </div>
          </div>
          <div className="relative h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl border-8 border-white">
            <img 
              src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?q=80&w=800&auto=format&fit=crop" 
              alt="Modern Clinic" 
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 right-4 bg-white p-4 rounded-xl shadow-lg border border-slate-100 flex items-center gap-3">
               <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xl font-bold">15+</div>
               <div>
                 <p className="text-sm font-bold text-slate-900">Years of</p>
                 <p className="text-xs text-slate-500 uppercase tracking-wider">Experience</p>
               </div>
            </div>
          </div>
        </div>
      </header>

      {/* 4. DOCTOR PROFILE (Crucial for conversions) */}
      <section id="about" className="py-20 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-12 flex flex-col md:flex-row gap-10 items-center">
          <div className="w-48 h-48 md:w-64 md:h-64 flex-shrink-0 rounded-full overflow-hidden border-4 border-teal-50 shadow-inner">
             <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?q=80&w=400&auto=format&fit=crop" alt="Dr. Sharma" className="w-full h-full object-cover"/>
          </div>
          <div className="space-y-4 text-center md:text-left">
            <h2 className="text-3xl font-bold text-slate-900">Dr. Rajesh Sharma</h2>
            <p className="text-teal-600 font-bold tracking-widest uppercase text-sm">BDS, MDS (Endodontics) | Ex-AIIMS</p>
            <p className="text-slate-600 leading-relaxed">
              With over 15 years of clinical excellence, Dr. Sharma specializes in painless root canals and full-mouth rehabilitations. Our clinic is dedicated to providing an anxiety-free environment where patient comfort is the ultimate goal.
            </p>
            <div className="pt-4 flex flex-wrap justify-center md:justify-start gap-3">
               <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded text-xs font-bold">Member of IDA</span>
               <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded text-xs font-bold">Gold Medalist</span>
               <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded text-xs font-bold">10,000+ Happy Patients</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. SERVICES GRID */}
      <section id="services" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Treatments & Specialties</h2>
            <p className="text-slate-500">World-class facilities equipped with the latest medical technology.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((svc, idx) => (
              <div key={idx} className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-teal-300 hover:shadow-lg transition group">
                <span className="text-4xl mb-6 block group-hover:scale-110 transition-transform">{svc.icon}</span>
                <h3 className="text-xl font-bold text-slate-800 mb-2">{svc.name}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. APPOINTMENT FORM & CONTACT (Side by Side) */}
      <section id="contact" className="py-24 px-6 bg-slate-800 text-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
          
          {/* Clinic Info */}
          <div className="space-y-10">
            <div>
              <h2 className="text-4xl font-bold mb-4">Visit Our Clinic</h2>
              <p className="text-slate-400">Conveniently located with ample parking space. Walk-ins are welcome, but prior appointments are highly recommended.</p>
            </div>
            
            <div className="space-y-6">
              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center text-teal-400 text-xl flex-shrink-0">📍</div>
                <div>
                  <h4 className="font-bold text-lg">Address</h4>
                  <p className="text-slate-400 mt-1">Ground Floor, Health Square Building,<br/>Sector 14, City Center, 110001</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center text-teal-400 text-xl flex-shrink-0">📞</div>
                <div>
                  <h4 className="font-bold text-lg">Contact Us</h4>
                  <p className="text-slate-400 mt-1">Reception: +91 98765 43210<br/>Emergency: +91 99999 88888</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center text-teal-400 text-xl flex-shrink-0">🕒</div>
                <div>
                  <h4 className="font-bold text-lg">Working Hours</h4>
                  <p className="text-slate-400 mt-1">Monday - Saturday: 9:00 AM - 8:00 PM<br/>Sunday: Closed (Emergencies Only)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div id="appointment" className="bg-white text-slate-800 p-8 sm:p-10 rounded-3xl shadow-2xl">
            <h3 className="text-2xl font-bold mb-6">Book an Appointment</h3>
            {isBooked ? (
              <div className="bg-teal-50 border border-teal-200 text-teal-700 p-6 rounded-xl text-center h-full flex flex-col justify-center items-center space-y-3">
                <span className="text-5xl">✅</span>
                <h4 className="font-bold text-xl">Request Sent!</h4>
                <p className="text-sm">Our receptionist will call you shortly to confirm your exact time slot.</p>
              </div>
            ) : (
              <form onSubmit={handleBooking} className="space-y-5">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Patient Name</label>
                  <input required type="text" className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition" placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Phone Number</label>
                  <input required type="tel" className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-lg focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition" placeholder="+91 00000 00000" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Preferred Date</label>
                    <input required type="date" className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-lg focus:outline-none focus:border-teal-500 transition text-slate-700" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Service</label>
                    <select className="w-full bg-slate-50 border border-slate-200 px-4 py-3 rounded-lg focus:outline-none focus:border-teal-500 transition text-slate-700">
                      <option>General Checkup</option>
                      <option>Dental / Root Canal</option>
                      <option>Skin / Dermatology</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <button type="submit" className="w-full bg-teal-600 text-white font-bold text-sm uppercase tracking-widest py-4 rounded-lg hover:bg-teal-700 transition shadow-lg mt-4">
                  Request Slot
                </button>
              </form>
            )}
          </div>

        </div>
      </section>

      {/* 7. SIMPLE FOOTER */}
      <footer className="bg-slate-900 text-slate-400 py-8 px-6 text-center text-sm border-t border-slate-800">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p>© {new Date().getFullYear()} ApexCare Multispecialty Clinic.</p>
          <p>Freelance Portfolio Demo</p>
        </div>
      </footer>

    </div>
  );
};

export default ClinicBasic;